"use client";

import { type JSX, useEffect, useRef } from "react";

/**
 * Probability-density strip from a two-component complex superposition ψ = e^{iφ₁} + r·e^{iφ₂},
 * so |ψ|² shows quantum-style interference (not a single traveling sine). Matches landing quantum
 * accents: cyan / fuchsia / brand orange on dark chrome.
 */
export function FooterQuantumWave(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let t = 0;
    const k = 0.018;
    let omega1 = reduceMotion ? 0 : 0.00135;
    let omega2 = reduceMotion ? 0 : -0.0017;
    const r = 0.58;

    const rho = (x: number, time: number): number => {
      const phi1 = k * x - omega1 * time;
      const phi2 = 1.35 * k * x + omega2 * time;
      const c = Math.cos(phi1) + r * Math.cos(phi2);
      const s = Math.sin(phi1) + r * Math.sin(phi2);
      return c * c + s * s;
    };

    const rePsi = (x: number, time: number): number =>
      Math.cos(k * x - omega1 * time) + r * Math.cos(1.35 * k * x + omega2 * time);

    const drawFrame = (): void => {
      const w = canvas.clientWidth;
      const h = 88;
      if (w < 1) return;

      ctx.clearRect(0, 0, w, h);

      const mid = h * 0.52;
      const amp = h * 0.38;
      const steps = Math.min(480, Math.max(120, Math.floor(w / 2)));

      let minR = Infinity;
      let maxR = -Infinity;
      const samples: number[] = [];
      for (let i = 0; i <= steps; i++) {
        const x = (i / steps) * w;
        const v = rho(x, t);
        samples.push(v);
        minR = Math.min(minR, v);
        maxR = Math.max(maxR, v);
      }
      const span = Math.max(maxR - minR, 1e-6);

      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, "rgba(34, 211, 238, 0.14)");
      grad.addColorStop(0.35, "rgba(192, 132, 252, 0.1)");
      grad.addColorStop(0.65, "rgba(255, 90, 31, 0.09)");
      grad.addColorStop(1, "rgba(34, 211, 238, 0.12)");

      ctx.beginPath();
      ctx.moveTo(0, h);
      for (let i = 0; i <= steps; i++) {
        const x = (i / steps) * w;
        const n = (samples[i]! - minR) / span;
        ctx.lineTo(x, mid - n * amp);
      }
      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.beginPath();
      for (let i = 0; i <= steps; i++) {
        const x = (i / steps) * w;
        const y = mid - ((rePsi(x, t) / (1 + r)) * amp * 0.42);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = "rgba(34, 211, 238, 0.42)";
      ctx.lineWidth = 1.25;
      ctx.stroke();

      ctx.beginPath();
      for (let i = 0; i <= steps; i++) {
        const x = (i / steps) * w;
        const y =
          mid -
          ((Math.sin(k * x - omega1 * t) + r * Math.sin(1.35 * k * x + omega2 * t)) / (1 + r)) *
            amp *
            0.38;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = "rgba(217, 70, 239, 0.28)";
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    const loop = (): void => {
      if (canvas.clientWidth < 1) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      drawFrame();
      t += 16;
      rafRef.current = requestAnimationFrame(loop);
    };

    const resize = (): void => {
      const parent = canvas.parentElement;
      const w = parent?.clientWidth ?? canvas.offsetWidth;
      const h = 88;
      const dpr = Math.min(window.devicePixelRatio ?? 1, 2);
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.floor(h * dpr);
      canvas.style.height = `${h}px`;
      canvas.style.width = "100%";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cancelAnimationFrame(rafRef.current);
      if (reduceMotion) {
        drawFrame();
      } else {
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement ?? canvas);

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotionPreference = (): void => {
      reduceMotion = mq.matches;
      omega1 = reduceMotion ? 0 : 0.00135;
      omega2 = reduceMotion ? 0 : -0.0017;
      cancelAnimationFrame(rafRef.current);
      if (reduceMotion) {
        drawFrame();
      } else {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    mq.addEventListener("change", onMotionPreference);

    const cleanup = (): void => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      mq.removeEventListener("change", onMotionPreference);
    };
    return cleanup;
  }, []);

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[5.5rem] overflow-hidden opacity-[0.95] motion-reduce:opacity-80"
      aria-hidden
    >
      <canvas
        ref={canvasRef}
        className="block w-full"
        style={{ mixBlendMode: "screen" }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-black"
        aria-hidden
      />
    </div>
  );
}
